import { IResortTopRated, ResortTopRated } from 'api/home/types';
import { RegularAd } from 'googlead/components/RegularAd';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import 'swiper/css';
import { isValidResponse } from 'util/isValidResponse';
import { fetchResortTopRating } from 'api/home/static';
import { fetchRegionResortTopRating } from 'api/region/static';
import { Diagnostics } from 'util/diagnostics';
import { ResortInfo } from 'components/home/BestResorts/ResortInfo';
import classNames from 'classnames';
import { ReactComponent as SvgArrowWhiteBg } from 'public/assets/icons/arrow-white-bg.svg';
import styles from './styles.module.scss';

SwiperCore.use([Navigation]);

const DEBUG_TAG_HOME: string = 'homepage-best-resorts';

export const HomeBestResorts = ({
  resortTopRatingOverall,
  activeTab,
  regionUUID,
  pageType,
}: {
  resortTopRatingOverall: Array<IResortTopRated>,
  activeTab: ResortTopRated,
  regionUUID?: string,
  pageType?: 'global' | undefined,
}) => {
  const [isLoading, toggleLoading] = useState<boolean>(false);
  const [resortTopRating, setResortTopRating] = useState<Array<IResortTopRated>>(resortTopRatingOverall);
  const sliderRef = useRef<SwiperCore | null>(null);

  useEffect(() => {
    setResortTopRating(resortTopRatingOverall);
  }, [resortTopRatingOverall]);

  const checkValidResponse = (response: Array<IResortTopRated>) => (
    response.every((item) => isValidResponse(item, ['resort', 'resortPath']))
  );

  const loadNewResortList = useCallback(async () => {
    toggleLoading(true);
    const response = regionUUID ? (
      await fetchRegionResortTopRating(regionUUID, activeTab)
    ) : (
      await fetchResortTopRating(activeTab)
    );

    if (!checkValidResponse(response)) {
      Diagnostics.message({ message: 'Invalid data received', tag: DEBUG_TAG_HOME });
      return;
    }

    setResortTopRating(response);
    toggleLoading(false);
  }, [activeTab, setResortTopRating, regionUUID]);

  const showResortsList = useMemo(() => (
    resortTopRating.map((item, i) => (
      <React.Fragment key={item.resort.uuid}>
        <ResortInfo
          isLoading={isLoading}
          index={i + 1}
          data={item}
        />
        {
          pageType !== 'global' && !regionUUID && i === 2 && (
            <RegularAd
              className={styles.unit}
              wrapperClassName={styles.wrapperAdBlock}
            />
          )
        }
        {
          pageType !== 'global' && regionUUID && (i === 3 || i === 9) && (
            <RegularAd
              className={styles.unit}
              wrapperClassName={styles.wrapperAdBlock}
            />
          )
        }
      </React.Fragment>
    ))
  ), [resortTopRatingOverall, activeTab, regionUUID, isLoading]);

  useEffect(() => {
    loadNewResortList();
  }, [activeTab]);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.slideNext();
  }, []);

  const sliderItems = React.Children.toArray(showResortsList);
  const shouldShowNavigation = resortTopRating.length > 4;

  return (
    <div className={classNames(styles.resortList, regionUUID && styles.w50)}>
      <div className={classNames(styles.sliderContainer, isLoading ? styles.isLoading : undefined)}>
        {shouldShowNavigation && (
          <>
            <button
              type="button"
              className={classNames(styles.swiperNavButton, styles.swiperButtonPrev)}
              onClick={handlePrev}
            >
              <SvgArrowWhiteBg />
            </button>
            <button
              type="button"
              className={classNames(styles.swiperNavButton, styles.swiperButtonNext)}
              onClick={handleNext}
            >
              <SvgArrowWhiteBg />
            </button>
          </>
        )}
        <Swiper
          spaceBetween={16}
          breakpoints={{
            375: { slidesPerView: 1.3 },
            640: { slidesPerView: 1.7 },
            768: { slidesPerView: 2 },
            880: { slidesPerView: 2.5 },
            1200: { slidesPerView: 3 },
            1400: { slidesPerView: 4 },
          }}
          className={styles.mobileSwiper}
          onSwiper={(swiperInstance) => {
            sliderRef.current = swiperInstance;
          }}
        >
          {sliderItems.map((element) => (
            <SwiperSlide
              key={String((element as React.ReactElement).key)}
              className={styles.mobileSlide}
            >
              {element}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};
